import { type ApolloClient } from "@apollo/client";
import {
  GetDeviceDocument,
  type GetDeviceQuery,
  type GetDeviceQueryVariables,
  GetLayoutDocument,
  type GetLayoutQuery,
  type GetLayoutQueryVariables,
  GetLocationDocument,
  type GetLocationQuery,
  type GetLocationQueryVariables,
  GetMediaDocument,
  GetMediaDownloadUrlDocument,
  type GetMediaDownloadUrlQuery,
  type GetMediaDownloadUrlQueryVariables,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  GetPostDocument,
  GetPostListFullDocument,
  type GetPostListFullQuery,
  type GetPostListFullQueryVariables,
  type GetPostQuery,
  type GetPostQueryVariables,
  GetProfileDocument,
  type GetProfileQuery,
  type GetProfileQueryVariables,
  GetServiceListFullDocument,
  type GetServiceListFullQuery,
  type GetServiceListFullQueryVariables,
  GetTagDocument,
  GetTagListFullDocument,
  type GetTagListFullQuery,
  type GetTagListFullQueryVariables,
  type GetTagQuery,
  type GetTagQueryVariables,
  IconType,
  type Media,
  type PostFilter,
  type PostTileAsset,
  type ProfileLayoutModel,
} from "@package/ui-gql/index-data-access";
import { chunk, get, uniq } from "lodash";
import { EventEmitter } from "../utils/event-emitter";
import { createMediaCacheName } from "../utils/media-utils";
import { getMedia, getMediaDownloadUrl } from "../utils/wall-utils-gql";

const HYDRATION_DELAY_MS = 3 * 1000;

// Trivia is the only flyout whose media count scales with authored content — every question and
// every answer can carry its own image — so its ids are hydrated in fixed-size batches rather
// than all at once. Every other flyout has an O(1) media count and hydrates directly.
const TRIVIA_MEDIA_BATCH_SIZE = 5;

type CacheEngineEvents = {
  "cache-started": () => void;
  "cache-updated": () => void;
};

export class WallEngineCache extends EventEmitter<CacheEngineEvents> {
  private hydratedHashKeys: Set<string> = new Set();
  private timeoutHydrationDelay?: ReturnType<typeof setTimeout>;
  private mediaObjectUrlCache: Map<string, Promise<string | void>> = new Map();

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly location?: string,
    private readonly device?: string,
    private readonly profile?: string,
  ) {
    super();

    if (!this.location && !this.profile) {
      throw new Error("A location or profile is required");
    }
  }

  public async activate(): Promise<void> {
    this.cancelTimeoutHydrationDelay();

    this.timeoutHydrationDelay = setTimeout(
      () => this.hydrate(),
      HYDRATION_DELAY_MS,
    );
  }

  public destroy(): void {
    super.destroy();
    this.cancelTimeoutHydrationDelay();
    console.log("[wall-engine-cache] Destroyed");
  }

  public async fetchMediaUrl(media: string): Promise<string | void> {
    if (this.mediaObjectUrlCache.has(media)) {
      return this.mediaObjectUrlCache.get(media) as Promise<string | void>;
    }

    const mediaPromise = new Promise<string | void>((resolve) => {
      setTimeout(async () => {
        const mediaModel = await getMedia(this.client, media);
        const mediaUrl = await getMediaDownloadUrl(this.client, media);
        if (mediaModel && mediaUrl) {
          const url = await window.electron?.ipcFetchMediaUrl(
            createMediaCacheName(mediaModel),
            mediaUrl.url,
          );

          if (url) {
            resolve(url);
            return;
          }
        }

        resolve();
      });
    });

    this.mediaObjectUrlCache.set(media, mediaPromise);

    return mediaPromise;
  }

  private cancelTimeoutHydrationDelay() {
    if (this.timeoutHydrationDelay) {
      clearTimeout(this.timeoutHydrationDelay);
    }
    this.timeoutHydrationDelay = undefined;
    console.log("[wall-engine-cache] Cancelled hydration delay timeout");
  }

  private async hydrate(): Promise<void> {
    console.log("[wall-engine-cache] Hydrating...");

    this.hydratedHashKeys.clear();

    this.emit("cache-started");

    if (this.device) {
      await this.hydrateDevice(this.device);
    }
    if (this.profile) {
      await this.hydrateProfile(this.profile);
    }
    if (this.location) {
      await this.hydrateLocation(this.location);
    }

    await this.hydrateServices();

    this.emit("cache-updated");
    console.log("[wall-engine-cache] Hydrated");
  }

  private async hydrateDevice(id: string): Promise<void> {
    if (this.checkHydrated("device", id)) {
      return;
    }

    try {
      await this.client.query<GetDeviceQuery, GetDeviceQueryVariables>({
        query: GetDeviceDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateLocation(id: string): Promise<void> {
    if (this.checkHydrated("location", id)) {
      return;
    }

    try {
      const location = await this.client.query<
        GetLocationQuery,
        GetLocationQueryVariables
      >({
        query: GetLocationDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });

      if (
        location?.data?.item?.profile &&
        location?.data?.item?.profile !== this.profile
      ) {
        await this.hydrateProfile(location.data.item.profile);
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateProfile(id: string): Promise<void> {
    if (this.checkHydrated("profile", id)) {
      return;
    }

    try {
      const profile = await this.client.query<
        GetProfileQuery,
        GetProfileQueryVariables
      >({
        query: GetProfileDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });

      if (profile?.data?.item?.background?.backgroundMedia) {
        await Promise.all(
          profile.data.item.background.backgroundMedia
            .filter((media) => !!media)
            .map((media) => this.hydrateMedia(media)),
        );
      }
      if (profile?.data?.item?.resting?.items) {
        await Promise.all(
          profile.data.item.resting.items
            .filter((item) => !!item?.media)
            .map((item) => this.hydrateMedia(item.media as string)),
        );
      }
      if (profile?.data?.item?.layouts) {
        await Promise.all(
          profile.data.item.layouts
            .filter((layout) => !!layout.layout)
            .map((layout) => this.hydrateProfileLayoutModel(layout)),
        );
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateMedia(id: string): Promise<void> {
    if (this.checkHydrated("media", id)) {
      return;
    }

    try {
      const media = await this.client.query<
        GetMediaQuery,
        GetMediaQueryVariables
      >({
        query: GetMediaDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });

      if (media?.data?.item?.file && !media?.data?.item?.isDir) {
        await this.hydrateMediaDownloadUrl(media.data.item);
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateMediaDownloadUrl(media: Media): Promise<void> {
    if (!media?.file) {
      return;
    }

    if (this.checkHydrated("media-url", media.file)) {
      return;
    }

    try {
      const mediaUrl = await this.client.query<
        GetMediaDownloadUrlQuery,
        GetMediaDownloadUrlQueryVariables
      >({
        query: GetMediaDownloadUrlDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          fileName: media.file,
        },
      });

      if (mediaUrl?.data?.item?.url) {
        await window.electron?.ipcFetchMediaUrl(
          createMediaCacheName(media),
          mediaUrl.data.item.url,
        );
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateProfileLayoutModel(
    layout: ProfileLayoutModel,
  ): Promise<void> {
    let idsPost: string[] = [];
    let idsTag: string[] = [];

    if (layout.layout) {
      this.hydrateLayout(layout.layout);

      if (layout.detailed) {
        for (let index = 0; index < (layout.items?.length ?? 0); index++) {
          const position = layout.items?.[index];
          if (position) {
            try {
              const posts = await this.client.query<
                GetPostListFullQuery,
                GetPostListFullQueryVariables
              >({
                query: GetPostListFullDocument,
                fetchPolicy: "network-only",
                errorPolicy: "ignore",
                variables: {
                  filter: {
                    enabled: true,
                    layouts: [layout.layout],
                    tags: (position.tags?.filter((t) => !!t) as string[]) ?? [],
                  },
                },
              });

              idsPost = idsPost.concat(
                (posts?.data?.items
                  ?.map((p) => p?.id)
                  ?.filter((i) => !!i) as string[]) ?? [],
              );
            } catch (e) {
              console.error("[wall-engine-cache]", e);
            }
          }
        }
      } else {
        try {
          const posts = await this.client.query<
            GetPostListFullQuery,
            GetPostListFullQueryVariables
          >({
            query: GetPostListFullDocument,
            fetchPolicy: "network-only",
            errorPolicy: "ignore",
            variables: {
              filter: {
                enabled: true,
                layouts: [layout.layout],
                tags: (layout.item?.tags?.filter((t) => !!t) as string[]) ?? [],
              },
            },
          });

          idsPost = idsPost.concat(
            (posts?.data?.items
              ?.map((p) => p?.id)
              ?.filter((i) => !!i) as string[]) ?? [],
          );
        } catch (e) {
          console.error("[wall-engine-cache]", e);
        }
      }
    }

    idsPost = idsPost
      .concat((layout.item?.posts?.filter((p) => !!p) as string[]) ?? [])
      .concat(
        (layout.items
          ?.map((i) => i?.posts ?? [])
          ?.flat()
          ?.filter((p) => !!p) as string[]) ?? [],
      );
    idsTag = idsTag
      .concat((layout.item?.tags?.filter((t) => !!t) as string[]) ?? [])
      .concat(
        (layout.items
          ?.map((i) => i?.tags ?? [])
          ?.flat()
          ?.filter((t) => !!t) as string[]) ?? [],
      );

    await Promise.all(uniq(idsPost).map((p) => this.hydratePost(p)));
    await Promise.all(uniq(idsTag).map((t) => this.hydrateTag(t)));
  }

  private async hydrateLayout(id: string): Promise<void> {
    if (this.checkHydrated("layout", id)) {
      return;
    }

    try {
      await this.client.query<GetLayoutQuery, GetLayoutQueryVariables>({
        query: GetLayoutDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydratePost(id: string): Promise<void> {
    if (this.checkHydrated("post", id)) {
      return;
    }

    try {
      const post = await this.client.query<GetPostQuery, GetPostQueryVariables>(
        {
          query: GetPostDocument,
          fetchPolicy: "network-only",
          errorPolicy: "ignore",
          variables: {
            id,
          },
        },
      );

      if (post?.data?.item?.relatedPosts) {
        await Promise.all(
          post.data.item.relatedPosts.map((p) => this.hydratePost(p)),
        );
      }
      if (post?.data?.item?.relatedPostAsset) {
        await this.hydrateMedia(post.data.item.relatedPostAsset);
      }
      if (post?.data?.item?.tags) {
        await Promise.all(post.data.item.tags.map((t) => this.hydrateTag(t)));
      }
      if (post?.data?.item?.tileAssets) {
        await Promise.all(
          post.data.item.tileAssets
            .filter((a) => !!a)
            .map((a) => this.hydrateTileAsset(a)),
        );
      }
      if (post?.data?.item?.flyoutType) {
        await this.hydratePostFlyout(post.data.item);
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydratePostFlyout(post: GetPostQuery["item"]) {
    switch (post.flyoutType) {
      // Standard
      case "60042c78003c346c54a26814":
        {
          const media = get(post, "customData.flyout.standard.media");
          if (media) {
            await this.hydrateMedia(media);
          }
        }
        break;
      // PDF
      case "60e5f1a78281542d34376c15":
        {
          const media = get(post, "customData.pdf.media");
          if (media) {
            await this.hydrateMedia(media);
          }
        }
        break;
      // Search
      case "60942b68093c366c54a2681d":
        {
          const mediaHeader = get(post, "customData.search.headerMedia");
          if (mediaHeader) {
            await this.hydrateMedia(mediaHeader);
          }

          const tags = get(post, "customData.search.tags");
          if (Array.isArray(tags)) {
            try {
              await this.client.query<
                GetTagListFullQuery,
                GetTagListFullQueryVariables
              >({
                query: GetTagListFullDocument,
                fetchPolicy: "network-only",
                errorPolicy: "ignore",
                variables: {
                  filter: {
                    ids: tags.filter((t) => !!t),
                  },
                  page: 0,
                  perPage: 1000,
                },
              });
            } catch (e) {
              console.error("[wall-engine-cache]", e);
            }

            await Promise.all(tags.map((t) => this.hydrateTag(t)));

            await this.hydratePostList({
              enabled: true,
              hideFromSearch: false,
              tags: (tags.filter((t) => !!t) as string[]) ?? [],
              q: undefined,
            });

            await Promise.all(
              tags.map((t) =>
                this.hydratePostList({
                  enabled: true,
                  hideFromSearch: false,
                  tags: [t],
                  q: undefined,
                }),
              ),
            );
          }
        }
        break;
      // Trivia
      case "66a9203f29411eff6ca74e5b":
        {
          const mediaIds = [
            get(post, "customData.backgroundMedia"),
            get(post, "customData.trivia.theme.logoMedia"),
            get(post, "customData.trivia.welcome.media"),
            ...(
              get(post, "customData.trivia.questions", []) as unknown[]
            ).flatMap((question) => [
              get(question, "media"),
              ...(get(question, "answers", []) as unknown[]).map((answer) =>
                get(answer, "media"),
              ),
            ]),
          ].filter((id): id is string => !!id);

          for (const batch of chunk(mediaIds, TRIVIA_MEDIA_BATCH_SIZE)) {
            await Promise.all(batch.map((id) => this.hydrateMedia(id)));
          }
        }
        break;
      // Post
      case "60942b68093c366c54a26819":
        {
          const mediaAd = get(post, "customData.flyoutAdvertisement");
          if (mediaAd) {
            await this.hydrateMedia(mediaAd);
          }

          const contents = get(post, "customData.flyoutPost.contents");
          if (Array.isArray(contents)) {
            await Promise.all(
              contents
                .filter((c) => !!c?.media)
                .map((c) => this.hydrateMedia(c.media as string)),
            );
          }
        }
        break;
      // Event
      case "60942b68093c366c54a2681f":
        {
          const mediaBanner = get(post, "customData.flyoutEvent.mediaBanner");
          if (mediaBanner) {
            await this.hydrateMedia(mediaBanner);
          }

          const mediaBody = get(post, "customData.flyoutEvent.mediaBody");
          if (mediaBody) {
            await this.hydrateMedia(mediaBody);
          }

          const mediaLogo = get(post, "customData.flyoutEvent.mediaLogo");
          if (mediaLogo) {
            await this.hydrateMedia(mediaLogo);
          }
        }
        break;
      // Wipe Away
      case "60473c78003c346c84529564":
        {
          const mediaForeground = get(
            post,
            "customData.flyout.fullScreenMedia.mediaForeground",
          );
          if (mediaForeground) {
            await this.hydrateMedia(mediaForeground);
          }

          const mediaBackground = get(
            post,
            "customData.flyout.fullScreenMedia.mediaBackground",
          );
          if (mediaBackground) {
            await this.hydrateMedia(mediaBackground);
          }
        }
        break;
      default:
        break;
    }
  }

  private async hydratePostList(filter: PostFilter): Promise<void> {
    if (this.checkHydrated("post-list", JSON.stringify(filter))) {
      return;
    }

    try {
      const posts = await this.client.query<
        GetPostListFullQuery,
        GetPostListFullQueryVariables
      >({
        query: GetPostListFullDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          filter,
          page: 0,
          perPage: 1000,
        },
      });

      if (Array.isArray(posts?.data?.items)) {
        await Promise.all(
          posts.data.items
            .map((p) => p?.id)
            .filter((id) => !!id)
            .map((id) => this.hydratePost(id as string)),
        );
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateTag(id: string): Promise<void> {
    if (this.checkHydrated("tag", id)) {
      return;
    }

    try {
      const tag = await this.client.query<GetTagQuery, GetTagQueryVariables>({
        query: GetTagDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
        variables: {
          id,
        },
      });

      if (
        tag?.data?.item?.mediaIcon &&
        (tag?.data?.item?.iconType === IconType.Media ||
          tag?.data?.item?.iconType === IconType.Textmedia)
      ) {
        await this.hydrateMedia(tag.data.item.mediaIcon);
      }
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateServices(): Promise<void> {
    try {
      await this.client.query<
        GetServiceListFullQuery,
        GetServiceListFullQueryVariables
      >({
        query: GetServiceListFullDocument,
        fetchPolicy: "network-only",
        errorPolicy: "ignore",
      });
    } catch (e) {
      console.error("[wall-engine-cache]", e);
    }
  }

  private async hydrateTileAsset(asset: PostTileAsset) {
    if (asset.layout) {
      await this.hydrateLayout(asset.layout);
    }
    if (asset.media) {
      await this.hydrateMedia(asset.media);
    }
    if (asset.tileBackingImage) {
      await this.hydrateMedia(asset.tileBackingImage);
    }
  }

  private checkHydrated(prefix: string, id: string): boolean {
    const key = `${prefix}:${id}`;

    if (this.hydratedHashKeys.has(key)) {
      return true;
    }
    this.hydratedHashKeys.add(key);

    return false;
  }
}
